import {
  DataTypeLocator,
  InputValueContentNode,
  QueryConfig,
  QueryNodeUtils,
} from '@helo/cms'
import { If, List, Value, asyncFlow, flow, pipe, tap } from '@helo/utils'
import { StoreApi } from 'zustand'

import {
  AbortError,
  abortControllers,
  apolloClient,
} from '../../../services/apolloClient'
import { useAppStore } from '../appStore'
import { AuthSlice } from '../auth/authSlice'
import { CmsSlice, CmsSliceActions } from '../cms'
import { PersistenceSlice } from '../persistence/persistenceSlice'
import { ViewSlice } from '../view/viewSlice'
import { ViewSliceActions } from '../view/viewSlice.actions'
import { QuerySlice } from './querySlice'
import {
  QuerySliceParts,
  getQuery,
  setQueryNode,
  updateQueryNodeResponse,
} from './querySlice.parts'

export {
  getQuery,
  findDataTypeConfig as getQueryDefinition,
} from './querySlice.parts'
export type RegisterQueryScope = {}

const registerQuery = (
  {
    id: queryId,
    paginated = false,
    queryDefinitionId,
    variables = {},
  }: QueryConfig,
  storeApi: StoreApi<QuerySlice>,
) =>
  flow(
    flow.inputExtends<
      QuerySlice & AuthSlice & ViewSlice & CmsSlice & PersistenceSlice
    >(),
    store => {
      const existingQuery = getQuery(queryId)(store)
      if (existingQuery) return store

      const queryState = ViewSliceActions.getQueryState(queryId)(store)

      const variables = queryState?.variables ?? {}

      const queryNode = pipe(
        store,
        CmsSliceActions.getQueryDefinition(queryDefinitionId),
        QueryNodeUtils.createQueryNode(
          queryId,
          store.cms.baseDataTypeConfigs,
          store.cms.mapperConfigs,
        ),
        QueryNodeUtils.setInitialVariables(variables),
        QueryNodeUtils.updateVariables(
          variables,
          store.cms.baseDataTypeConfigs,
        ),
        QueryNodeUtils.updateVariables(
          queryState?.variables ?? {},
          store.cms.baseDataTypeConfigs,
        ),
        QueryNodeUtils.setupApolloObservables(apolloClient),
        If.then(
          () => !paginated,
          // ⚠️ APOLLO CLIENT NETWORK CALL TRIGGER POINT: startQueryAndSubscribe triggers a network request
          // This is called during registerQuery when !paginated
          QueryNodeUtils.startQueryAndSubscribe(
            result =>
              pipe(
                storeApi.getState(),
                updateQueryNodeResponse(result),
                storeApi.setState,
              ),
            error =>
              pipe(
                storeApi.getState(),
                updateQueryNodeResponse(error),
                storeApi.setState,
              ),
            store.cms.baseDataTypeConfigs,
          ),
        ),
        queryNode => {
          return {
            ...queryNode,
            paginated: paginated,
          }
        },
      )

      return pipe(store, setQueryNode(queryNode), updateViewQueryState(queryId))
    },
  )

export type UpdateQueryOptionsV2 = {
  queryId: string
  variables?: Record<string, any>
  variableLocator?: DataTypeLocator
  value?: any
}

const updateQueryInputValue = (
  queryId: string,
  inputValueContentNode: InputValueContentNode | InputValueContentNode[],
  storeApi: StoreApi<QuerySlice>,
) =>
  flow(
    flow.inputExtends<QuerySlice & ViewSlice & PersistenceSlice & CmsSlice>(),
    store => {
      const queryNode = getQuery(queryId)(store)
      if (!queryNode) return store

      return pipe(
        inputValueContentNode,
        If.then(
          () => !queryNode,
          () => pipe.return(store),
        ),
        If.isElse(
          Value.isArray,
          List.reduce(
            (acc, cn) =>
              QueryNodeUtils.updateQueryInputValue(
                cn,
                store.cms.baseDataTypeConfigs,
              )(acc),
            queryNode,
          ),
          v =>
            QueryNodeUtils.updateQueryInputValue(
              v,
              store.cms.baseDataTypeConfigs,
            )(queryNode),
        ),
        If.thenElse(
          updatedQueryNode =>
            (!updatedQueryNode.variablesHaveChanged && queryNode.hasRun) ||
            updatedQueryNode.paginated,
          updated => updated,
          If.thenElse(
            updatedQueryNode => !!updatedQueryNode.subscription,
            qn => {
              // ⚠️ APOLLO CLIENT NETWORK CALL TRIGGER POINT: refetch triggers a network request
              // This is called from updateQueryInputValue when subscription exists
              void QueryNodeUtils.refetch()(qn)
              return qn
            },
            qn => {
              // ⚠️ APOLLO CLIENT NETWORK CALL TRIGGER POINT: startQueryAndSubscribe triggers a network request
              // This is called from updateQueryInputValue when no subscription exists
              return QueryNodeUtils.startQueryAndSubscribe(
                result =>
                  pipe(
                    storeApi.getState(),
                    updateQueryNodeResponse(result),
                    storeApi.setState,
                  ),
                error =>
                  pipe(
                    storeApi.getState(),
                    updateQueryNodeResponse(error),
                    storeApi.setState,
                  ),
                store.cms.baseDataTypeConfigs,
              )(qn)
            },
          ),
        ),
        updatedQueryNode =>
          pipe(
            store,
            setQueryNode(updatedQueryNode),
            updateViewQueryState(queryId),
          ),
      )
    },
  )
const updateVariables = (
  queryId: string,
  variables: Record<string, unknown>,
  storeApi: StoreApi<QuerySlice>,
) =>
  flow(
    flow.inputExtends<QuerySlice & ViewSlice & PersistenceSlice & CmsSlice>(),
    store => {
      const queryNode = getQuery(queryId)(store)

      return pipe(
        queryNode,
        If.then(
          () => !queryNode,
          () => pipe.return(store),
        ),
        QueryNodeUtils.updateVariables(
          variables,
          store.cms.baseDataTypeConfigs,
        ),
        If.thenElse(
          updatedQueryNode =>
            (!updatedQueryNode.variablesHaveChanged && queryNode?.hasRun) ||
            updatedQueryNode.paginated,
          updated => updated,
          If.thenElse(
            updatedQueryNode => !!updatedQueryNode.subscription,
            qn => {
              // ⚠️ APOLLO CLIENT NETWORK CALL TRIGGER POINT: refetch triggers a network request
              // This is called from updateVariables when subscription exists
              void QueryNodeUtils.refetch()(qn)
              return qn
            },
            qn => {
              // ⚠️ APOLLO CLIENT NETWORK CALL TRIGGER POINT: startQueryAndSubscribe triggers a network request
              // This is called from updateVariables when no subscription exists
              return QueryNodeUtils.startQueryAndSubscribe(
                result =>
                  pipe(
                    storeApi.getState(),
                    updateQueryNodeResponse(result),
                    storeApi.setState,
                  ),
                error =>
                  pipe(
                    storeApi.getState(),
                    updateQueryNodeResponse(error),
                    storeApi.setState,
                  ),
                store.cms.baseDataTypeConfigs,
              )(qn)
            },
          ),
        ),
        updatedQueryNode =>
          pipe(
            store,
            setQueryNode(updatedQueryNode),
            updateViewQueryState(queryId),
          ),
      )
    },
  )

const updateQuery = (options: UpdateQueryOptionsV2) =>
  flow(
    flow.inputExtends<QuerySlice & ViewSlice & PersistenceSlice & CmsSlice>(),
    store => {
      const { queryId, variables, variableLocator, value } = options
      if (!queryId) return store

      const queryNode = getQuery(queryId)(store)
      if (!queryNode) return store

      const updatedQueryNode = pipe(queryNode, qn => {
        if (variableLocator) {
          return QueryNodeUtils.updateVariable(variableLocator, value)(qn)
        }
        if (variables) {
          return QueryNodeUtils.updateVariables(
            variables,
            store.cms.baseDataTypeConfigs,
          )(qn)
        }
        return qn
      })

      // Check if variables have changed using the flag from QueryNodeUtils
      if (!updatedQueryNode.variablesHaveChanged) {
        return store
      }

      // ⚠️ APOLLO CLIENT NETWORK CALL TRIGGER POINT: refetch triggers a network request
      // This is called from updateQuery when variables have changed
      QueryNodeUtils.refetch()(updatedQueryNode)
      const updatedQueryNodeWithSubscription = pipe(
        updatedQueryNode,
        queryNode.subscription
          ? qn => qn
          : // ⚠️ APOLLO CLIENT NETWORK CALL TRIGGER POINT: startQueryAndSubscribe triggers a network request
            // This is called from updateQuery when no subscription exists
            QueryNodeUtils.startQueryAndSubscribe(
              result =>
                pipe(
                  useAppStore.getState(),
                  updateQueryNodeResponse(result),
                  useAppStore.setState,
                ),
              error =>
                pipe(
                  useAppStore.getState(),
                  updateQueryNodeResponse(error),
                  useAppStore.setState,
                ),
              store.cms.baseDataTypeConfigs,
            ),
      )

      return pipe(
        store,
        setQueryNode(updatedQueryNodeWithSubscription),
        updateViewQueryState(queryId),
      )
    },
  )

const refetchQuery = (queryId: string, storeApi: StoreApi<QuerySlice>) =>
  flow(flow.inputExtends<QuerySlice & CmsSlice>(), store => {
    const queryNode = getQuery(queryId)(store)
    if (!queryNode) return

    try {
      return pipe(
        queryNode,
        QueryNodeUtils.refetch(),
        tap(updatedQueryNode => {
          pipe(
            storeApi.getState(),
            setQueryNode(updatedQueryNode),
            storeApi.setState,
          )
        }),
      )
    } catch (error) {
      pipe(
        storeApi.getState(),
        updateQueryNodeResponse(error as any),
        storeApi.setState,
      )
      throw error
    }
  })

const removeQuery = (queryId: string) =>
  flow(
    flow.inputExtends<QuerySlice>(),
    tap(store => {
      const queryNode = getQuery(queryId)(store)
      if (!queryNode) return
      queryNode?.subscription?.unsubscribe()
      const existingAbortController = abortControllers[queryId]
      existingAbortController?.abort(new AbortError('removing query'))
      delete abortControllers[queryId]
    }),
    store => {
      return {
        ...store,
        queryStore: {
          ...store.queryStore,
          queries: Object.fromEntries(
            Object.entries(store.queryStore.queries).filter(
              ([key]) => key !== queryId,
            ),
          ),
        },
      }
    },
  )

const removeAllQueries = () =>
  flow(
    flow.inputExtends<QuerySlice>(),
    <T extends QuerySlice>(store: T): T =>
      Object.keys(store.queryStore.queries).reduce<T>(
        (currentStore, queryId) => removeQuery(queryId)(currentStore),
        store,
      ),
  )

const resetPagination = (queryId: string, storeApi: StoreApi<QuerySlice>) =>
  flow(flow.inputExtends<QuerySlice & CmsSlice>(), store => {
    const queryNode = getQuery(queryId)(store)
    if (!queryNode) return store

    const resetQueryNode = pipe(
      queryNode,
      QueryNodeUtils.resetData(store.cms.baseDataTypeConfigs),
      QueryNodeUtils.updateVariables(
        { skip: 0 },
        store.cms.baseDataTypeConfigs,
      ),
    )

    return pipe(store, setQueryNode(resetQueryNode))
  })

const updateViewQueryState = (queryId: string) =>
  // take store and get current variables as an object and call the viewSlice action to update a particular query state.
  flow(
    flow.inputExtends<QuerySlice & ViewSlice & PersistenceSlice>(),
    <T extends QuerySlice & ViewSlice & PersistenceSlice>(store: T): T => {
      const queryNode = getQuery(queryId)(store)
      if (!queryNode) return store

      const mappedVariables =
        QueryNodeUtils.getMappedVariablesAsObject()(queryNode)

      const paginationKeys = [
        'after',
        'before',
        'first',
        'last',
        'skip',
        'take',
      ]

      const baseQueryState = ViewSliceActions.getQueryBaseState(queryId)(store)

      const prunedVariables = QuerySliceParts.pruneVariables(
        mappedVariables,
        baseQueryState?.variables ?? {},
      )

      return ViewSliceActions.setQueryState({
        queryId,
        variables: prunedVariables,
      })(store) as T
    },
  )

export const QuerySliceActions = {
  registerQuery,
  updateQuery,
  updateQueryInputValue,
  updateViewQueryState,
  updateVariables,
  refetchQuery,
  removeQuery,
  removeAllQueries,
  resetPagination,
}
